import { Download, Loader2, Paperclip, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { WorkspaceFile } from "../../sandbox/client/api";
import { FileName } from "../FileName";
import { panelName } from "../useThreadWorkspace";
import { PREVIEW_MAX_BYTES, previewKind } from "./kinds";
import { PREVIEWERS } from "./previewers";

/** A header action: its label beside the icon on a desktop, the icon alone on a phone. */
const ACTION = "h-8 shrink-0 gap-1.5 max-sm:h-10 max-sm:w-10 max-sm:px-0";

/**
 * The cross: where DialogContent's own sits on a desktop, and in the header row on a phone, as
 * large as the actions beside it. Its ring shows only for the keyboard: it takes focus when the
 * dialog opens, which a tap or a click on a row should not light up.
 */
const CLOSE =
  "rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:absolute sm:right-4 sm:top-4 max-sm:flex max-sm:size-10 max-sm:shrink-0 max-sm:items-center max-sm:justify-center max-sm:rounded-md";

type Load =
  | { status: "loading" }
  | { status: "ready"; bytes: ArrayBuffer }
  | { status: "tooLarge" }
  | { status: "failed" }
  | { status: "unsupported" };

const FALLBACKS: Record<Exclude<Load["status"], "loading" | "ready">, string> = {
  tooLarge: "Too large to preview.",
  failed: "Couldn't load the preview.",
  unsupported: "This file can't be previewed.",
};

/** A download or an add from the dialog that failed, said under its header. */
type Problem = { file: WorkspaceFile; text: string };

/**
 * A Files panel file, previewed by its kind (see `PREVIEWERS`), with its download always in the
 * header and, given `onToggleContext`, a toggle that puts it on the next message ("Added", pressed,
 * while `inContext`) and takes it off again. A file too large to preview is not fetched (nor shown,
 * when its bytes come to more than the listing said); one whose bytes are not what its name says,
 * or that fails to load, says so in place of the preview. A failed download or add says so here,
 * over the page. It opens with focus on the cross, as the chat's file preview does, never on an
 * action. Escape or the cross closes it, and focus goes back to the row. Full screen on a phone,
 * where the header's actions are icons only.
 */
export function WorkspacePreviewDialog({
  file,
  read,
  onDownload,
  inContext = false,
  onToggleContext,
  onClose,
}: {
  file: WorkspaceFile | null;
  read: (file: WorkspaceFile) => Promise<ArrayBuffer>;
  onDownload: (file: WorkspaceFile) => Promise<void>;
  inContext?: boolean;
  onToggleContext?: (file: WorkspaceFile) => Promise<void>;
  onClose: () => void;
}) {
  const [load, setLoad] = useState<Load>({ status: "loading" });
  // Shown while its file does (a late answer for another never shows), until the dialog closes or
  // shows another.
  const [problem, setProblem] = useState<Problem | null>(null);
  // Another file starts from loading: the last one's bytes never reach its previewer.
  const [shown, setShown] = useState(file);
  if (file !== shown) {
    setShown(file);
    setLoad({ status: "loading" });
    setProblem(null);
  }
  const kind = file ? previewKind(file) : null;
  const unsupported = useCallback(() => setLoad({ status: "unsupported" }), []);
  const tooLarge = useCallback(() => setLoad({ status: "tooLarge" }), []);
  // Opened without a DialogTrigger, so Radix has nothing to give focus back to: the opener (the row).
  const opener = useRef<HTMLElement | null>(null);
  const close = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!file) return;
    if (!kind) {
      setLoad({ status: "unsupported" });
      return;
    }
    const overCap = (size: number) => size > PREVIEW_MAX_BYTES[kind];
    if (overCap(file.size)) {
      setLoad({ status: "tooLarge" });
      return;
    }
    let stale = false;
    read(file).then(
      // The listing's size may be older than the bytes.
      (bytes) =>
        !stale &&
        setLoad(overCap(bytes.byteLength) ? { status: "tooLarge" } : { status: "ready", bytes }),
      () => !stale && setLoad({ status: "failed" })
    );
    return () => {
      stale = true;
    };
  }, [file, kind, read]);

  const name = file ? panelName(file.path) : "";
  const Previewer = kind ? PREVIEWERS[kind] : null;
  const download = file && (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={ACTION}
      onClick={() => {
        setProblem(null);
        onDownload(file).catch(() => setProblem({ file, text: `Couldn't download ${name}.` }));
      }}
    >
      <Download className="!size-3.5" aria-hidden />
      <span className="max-sm:sr-only">Download</span>
    </Button>
  );
  const toggleContext = file && onToggleContext && (
    <Button
      type="button"
      variant={inContext ? "default" : "outline"}
      size="sm"
      className={ACTION}
      aria-pressed={inContext}
      onClick={() => {
        setProblem(null);
        // Taking a file off never fails here (see `takeBackThreadFile`): only an add can.
        onToggleContext(file).catch(() => setProblem({ file, text: `Couldn't add ${name}.` }));
      }}
    >
      <Paperclip className="!size-3.5" aria-hidden />
      <span className="max-sm:sr-only">{inContext ? "Added" : "Add to context"}</span>
    </Button>
  );

  return (
    <Dialog open={file !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        onOpenAutoFocus={(event) => {
          opener.current = document.activeElement as HTMLElement | null;
          // The cross, not the first button: Add to context.
          if (!close.current) return;
          event.preventDefault();
          close.current.focus();
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          opener.current?.focus();
        }}
        className="flex h-[85vh] max-w-4xl flex-col gap-0 overflow-hidden p-0 max-sm:h-dvh max-sm:max-w-none max-sm:rounded-none max-sm:border-0"
      >
        <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-4 py-2.5 pr-12 text-left max-sm:pr-2 sm:items-start">
          {/* A long name wraps, never cut: it may be all that tells two files apart. The actions
              keep their place beside it, by its first line on a desktop. */}
          <DialogTitle className="min-w-0 flex-1 break-words text-sm sm:py-1.5">
            <FileName name={name} />
          </DialogTitle>
          {toggleContext}
          {download}
          <DialogClose ref={close} className={CLOSE}>
            <X className="h-4 w-4" aria-hidden />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        {file && problem?.file === file && (
          <p role="alert" className="border-b px-4 py-2 text-xs text-destructive">
            {problem.text}
          </p>
        )}
        {load.status === "loading" && (
          <div role="status" className="flex flex-1 items-center justify-center">
            <Loader2
              className="h-5 w-5 text-muted-foreground motion-safe:animate-spin"
              aria-hidden
            />
            <span className="sr-only">Loading preview</span>
          </div>
        )}
        {load.status === "ready" && Previewer && (
          <Previewer
            bytes={load.bytes}
            name={name}
            onUnsupported={unsupported}
            onTooLarge={tooLarge}
          />
        )}
        {load.status !== "loading" && load.status !== "ready" && (
          <p className="flex flex-1 items-center justify-center p-8 text-sm text-muted-foreground">
            {FALLBACKS[load.status]}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
